import { useContext, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import {
  ArrowLeftIcon,
  ChevronIcon,
  CloudDownloadIcon,
  CrossIcon,
  Expand01Icon,
  Send01Icon,
  SendIcon,
  ThreeDots,
  TrashIcon,
} from 'assets/icons';
import { ButtonStyle } from 'common/button';
import { NotificationType } from 'common/enums';
import { Menu } from 'common/menu';
import { ModalWarning } from 'common/modal-warning';
import { PrivateToggleItem } from 'common/private-toggle-item';
import { ShareDialog } from 'common/share-dialog';
import { apiBaseURL } from 'config/constants';
import { appActions, AppContext } from 'context/app-context';
import { downloadMemory } from 'helpers/download-memory';
import { useClickOutside } from 'hooks/click-outside';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import {
  AlbumContentSerialized,
  AlbumDetailType,
} from 'networking/types/album-detail';
import { goToPage, RouteName } from 'routes';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import { useBreadcrumbOrigin } from 'hooks/use-breadcrumb-origin';
import { getRouteFor } from 'routes/route-helpers';
import { classnames } from 'helpers/utils';
import { albumAccess, albumShareTarget, vaultAccess } from 'helpers/sharing';
import { ShareTarget } from 'networking/controllers/shares-controller';
import { MemoryTitle } from '../memory-title';
import styles from './album-sub-header.module.scss';

// One tooltip for the icon-only actions; each button names itself through
// data-tooltip-content.
const ACTIONS_TOOLTIP_ID = 'view-mode-actions-tooltip';

interface AlbumSubHeaderProps {
  album: AlbumDetailType | null;
  content: AlbumContentSerialized | undefined;
  vaultId?: number;
  isGuest: boolean;
  onDeleted?: (contentId: number) => void;
  // Whether the centered memory title can be renamed inline (owner only).
  editable?: boolean;
  // Called after a successful inline rename so the parent can patch its state.
  onRenamed?: (contentId: number, name: string) => void;
  // Called after other changes to the memory (e.g. making it private).
  onContentUpdated?: (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => void;
  // Public view mode (shared links, QR codes): only the memory's title, with
  // no navigation, sharing or memory actions.
  publicView?: boolean;
  // Phones: the mobile app's memory header. Back, then the actions as round
  // buttons, with full screen among them; the title is on the page instead.
  phone?: boolean;
  onFullScreen?: () => void;
}

export const AlbumSubHeader = ({
  album,
  content,
  vaultId,
  isGuest,
  onDeleted,
  editable = false,
  onRenamed,
  onContentUpdated,
  publicView = false,
  phone = false,
  onFullScreen,
}: AlbumSubHeaderProps) => {
  const { dispatch, state } = useContext(AppContext);
  const breadcrumbOrigin = useBreadcrumbOrigin();
  const [showShare, setShowShare] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  useClickOutside(moreRef, () => setShowMoreMenu(false));

  // What this person's role allows here (the API says per album and memory).
  const inAlbum = albumAccess(
    album,
    vaultAccess(state.data.user, state.currentVault),
  );
  const memoryAccess = content?.myAccess ?? {
    canEdit: inAlbum.role === 'editor',
    canDescribe: inAlbum.role === 'editor',
    canSetPrivacy: inAlbum.role === 'editor',
    canShare: inAlbum.canShare,
  };
  // The memory on screen, then its album: whichever they may share.
  const shareChoices: ShareTarget[] = [
    ...(content && memoryAccess.canShare && vaultId
      ? [
          {
            type: 'memory' as const,
            vaultId,
            id: content.id,
            name: content.name || 'This memory',
            isPrivate: content.isPrivate || album?.isPrivate,
          },
        ]
      : []),
    ...(album && inAlbum.canShare && vaultId
      ? [albumShareTarget(vaultId, album)]
      : []),
  ];

  // The … menu belongs to the memory it was opened for.
  useEffect(() => {
    setShowMoreMenu(false);
  }, [content?.id]);

  const goBack = () => {
    if (vaultId && album) {
      // Return to the single-album (grid/planner) screen, keeping its trail.
      goToPage(
        RouteName.AlbumScreen,
        { id: vaultId, albumId: album.id },
        {},
        withOrigin(breadcrumbOrigin),
      );
    } else if (vaultId) {
      goToPage(RouteName.VaultDetail, { id: vaultId });
    } else {
      window.history.back();
    }
  };

  const notify = (title: string, type: NotificationType, message?: string) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        message,
        type,
      }),
    });

  const handleDownload = async () => {
    if (!content) return;
    try {
      await downloadMemory(content);
    } catch (error) {
      notify(
        'Download failed',
        NotificationType.Error,
        error instanceof Error ? error.message : 'Please try again.',
      );
    }
  };

  const handleDelete = async () => {
    if (!content) return;
    setShowDeleteModal(false);
    try {
      await VaultsController.deleteAlbumContent(
        content.id,
        content.fileSize ?? 0,
      );
      onDeleted?.(content.id);
      notify('Content deleted', NotificationType.Success);
    } catch (error) {
      notify('Failed to delete content', NotificationType.Error);
    }
  };

  const handleTogglePrivate = async () => {
    if (!content) return;
    const isPrivate = !content.isPrivate;
    try {
      setSavingPrivacy(true);
      await VaultsController.updateContentMetadata(content.id, { isPrivate });
      onContentUpdated?.(content.id, { isPrivate });
      notify(
        isPrivate ? 'Memory is now private' : 'Memory is no longer private',
        NotificationType.Success,
      );
    } catch (error) {
      notify('The memory could not be updated', NotificationType.Error);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const inviterName = state.data.user.name || '';
  const getContributorLink = () => {
    if (album?.guestInviteToken) {
      return `${apiBaseURL}${getRouteFor(
        RouteName.AlbumViewMode,
        { albumId: album.id },
        {
          token: album.guestInviteToken,
          // Shown in the sign-up box: who invited them.
          inviter: btoa(unescape(encodeURIComponent(inviterName))),
        },
      )}`;
    }
    return '';
  };

  return (
    <div className={classnames(styles.subHeader, { [styles.phone]: phone })}>
      <div className={styles.left}>
        {!publicView && (
          <button
            type="button"
            className={styles.backButton}
            aria-label="Back"
            onClick={goBack}
          >
            {phone ? (
              <ArrowLeftIcon />
            ) : (
              <ChevronIcon className={styles.backIcon} />
            )}
          </button>
        )}
      </div>

      {!phone && (
        <div className={styles.titleWrap}>
          <MemoryTitle
            content={content}
            editable={editable}
            onRenamed={onRenamed}
          />
        </div>
      )}

      <div className={styles.actions}>
        {!publicView && shareChoices.length > 0 && (
          <div className={styles.shareWrap}>
            <button
              type="button"
              className={styles.actionButton}
              aria-label="Share"
              data-tooltip-id={ACTIONS_TOOLTIP_ID}
              data-tooltip-content="Share"
              onClick={() => setShowShare(true)}
            >
              {phone ? <Send01Icon /> : <SendIcon />}
            </button>
          </div>
        )}
        {phone && onFullScreen && content && (
          <button
            type="button"
            className={styles.actionButton}
            aria-label="View full screen"
            onClick={onFullScreen}
          >
            <Expand01Icon />
          </button>
        )}
        {!publicView && !isGuest && content && (
          <button
            type="button"
            className={styles.actionButton}
            aria-label="Download"
            data-tooltip-id={ACTIONS_TOOLTIP_ID}
            data-tooltip-content="Download"
            onClick={handleDownload}
          >
            <CloudDownloadIcon />
          </button>
        )}
        {!publicView && !isGuest && content && memoryAccess.canEdit && (
          <button
            type="button"
            className={classnames(styles.actionButton, styles.deleteButton)}
            aria-label="Delete"
            data-tooltip-id={ACTIONS_TOOLTIP_ID}
            data-tooltip-content="Delete"
            onClick={() => setShowDeleteModal(true)}
          >
            <TrashIcon />
          </button>
        )}
        {!publicView && !isGuest && content && memoryAccess.canSetPrivacy && (
          <div className={styles.moreWrap} ref={moreRef}>
            <button
              type="button"
              className={styles.actionButton}
              aria-label="More"
              // The menu holds only the privacy switch, so say what it does;
              // no tooltip while the menu is open, so it doesn't cover it.
              data-tooltip-id={showMoreMenu ? undefined : ACTIONS_TOOLTIP_ID}
              data-tooltip-content={
                content.isPrivate ? 'Make public' : 'Make private'
              }
              onClick={() => setShowMoreMenu((prev) => !prev)}
            >
              <ThreeDots className={styles.moreIcon} />
            </button>
            {showMoreMenu && (
              <Menu position="" className={styles.moreMenu}>
                <PrivateToggleItem
                  isPrivate={content.isPrivate}
                  onToggle={handleTogglePrivate}
                  disabled={savingPrivacy}
                />
              </Menu>
            )}
          </div>
        )}
        {!publicView && !phone && (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              className={styles.actionButton}
              aria-label="Close"
              onClick={goBack}
            >
              <CrossIcon />
            </button>
          </>
        )}
        <Tooltip
          id={ACTIONS_TOOLTIP_ID}
          place="bottom"
          className={styles.actionTooltip}
        />
      </div>

      {showDeleteModal && (
        <ModalWarning
          title="Delete content"
          content="Are you sure you want to delete this Entry? This action cannot be undone."
          successButtonText="Delete"
          successStyleButton={ButtonStyle.DestructivePrimary}
          closeFn={() => setShowDeleteModal(false)}
          successFn={handleDelete}
        />
      )}

      {showShare && shareChoices.length > 0 && (
        <ShareDialog
          target={shareChoices[0]}
          choices={shareChoices}
          contributorLink={getContributorLink() || undefined}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
};
